import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Alert,
  Fade,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import { AuthContext } from "../../../context/AuthContext/AuthContext";
import LogoHeader from "../components/LogoHeader";
import PasswordRulesChecklist from "../components/PasswordRulesChecklist";
import LoginBack from "../../../assets/Images/LoginBack2.png";

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { changePassword, signOut } = useContext(AuthContext);

  const isForced = location.state?.forced === true;
  const reason = location.state?.reason || "You must change your password before continuing.";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const passwordMeetsRules =
    newPassword.length >= 8 &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(newPassword) &&
    /[0-9]/.test(newPassword) &&
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(newPassword);

  const isFormValid =
    currentPassword.length >= 8 &&
    passwordMeetsRules &&
    passwordsMatch &&
    currentPassword !== newPassword;

  const handleChangePassword = async () => {
    setError(null);

    if (currentPassword === newPassword) {
      setError("The new password must be different from the current password.");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const data = await changePassword(currentPassword, newPassword);

      if (data.result === 0) {
        setSuccess(true);
        // signOut es async (llama logoutV2 en el backend y sólo después limpia
        // user + localStorage). Si no lo esperamos, navigate corre antes de que
        // se limpie el user, y LoginPage ve forcePasswordChange=true y rebota
        // al empleado a /auth/change-password — queda pegado.
        setTimeout(async () => {
          await signOut();
          navigate("/Login", {
            state: {
              message: "Password changed successfully. Please sign in with your new password.",
              type: "success",
            },
          });
        }, 2500);
      } else {
        // Mapear códigos de error del backend
        switch (data.result) {
          case -1:
            setError(data.message); // Validación de complejidad
            break;
          case 1:
            setError("User not found. Please contact your administrator.");
            break;
          case 2:
            setError("Current password is incorrect.");
            break;
          case 3:
            setError("The new password must be different from the current password.");
            break;
          case 4:
            setError("You cannot reuse any of your last 6 passwords.");
            break;
          default:
            setError(data.message || "An error occurred. Please try again.");
        }
      }
    } catch (err) {
      const responseData = err?.response?.data;
      setError(responseData?.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isFormValid && !loading && !success) {
      handleChangePassword();
    }
  };

  const passwordFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#fafafa",
      "&:hover fieldset": { borderColor: "#00bcd4" },
      "&.Mui-focused fieldset": { borderColor: "#0C343D" },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundImage: `url(${LoginBack})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Fade in timeout={600}>
        <Card
          sx={{
            width: "100%",
            maxWidth: 440,
            borderRadius: 4,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
            bgcolor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            overflow: "hidden",
          }}
        >
          {loading && <LinearProgress sx={{ height: 3 }} />}

          <CardContent sx={{ px: 4, py: 4 }}>
            <LogoHeader />

            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#0C343D", textAlign: "center", mb: 0.5 }}
            >
              Change Password
            </Typography>

            {isForced && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                {reason}
              </Alert>
            )}

            {/* Success state */}
            {success ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CheckCircleOutline sx={{ fontSize: 64, color: "#2e7d32", mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32", mb: 1 }}>
                  Password Changed Successfully
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Redirecting to sign in...
                </Typography>
                <CircularProgress size={20} sx={{ mt: 2, color: "#2e7d32" }} />
              </Box>
            ) : (
              <>
                {error && (
                  <Fade in>
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      {error}
                    </Alert>
                  </Fade>
                )}

                {/* Current Password */}
                <TextField
                  type={showCurrent ? "text" : "password"}
                  label="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end" size="small">
                          {showCurrent ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={passwordFieldSx}
                />

                {/* New Password */}
                <TextField
                  type={showNew ? "text" : "password"}
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  error={newPassword.length > 0 && currentPassword === newPassword}
                  helperText={
                    newPassword.length > 0 && currentPassword === newPassword
                      ? "Must be different from current password"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew(!showNew)} edge="end" size="small">
                          {showNew ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={passwordFieldSx}
                />

                {/* Password rules checklist */}
                <PasswordRulesChecklist password={newPassword} />

                {/* Confirm Password */}
                <TextField
                  type={showConfirm ? "text" : "password"}
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  error={confirmPassword.length > 0 && !passwordsMatch}
                  helperText={
                    confirmPassword.length > 0 && !passwordsMatch
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" size="small">
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={passwordFieldSx}
                />

                {/* Submit Button */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleChangePassword}
                  disabled={!isFormValid || loading}
                  sx={{
                    mt: 3,
                    mb: 1,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    bgcolor: "#0C343D",
                    boxShadow: "0 4px 12px rgba(12,52,61,0.3)",
                    "&:hover": {
                      bgcolor: "#0a2a33",
                      boxShadow: "0 6px 16px rgba(12,52,61,0.4)",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#ccc",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    "Change Password"
                  )}
                </Button>

                {/* Back to login (only if not forced) */}
                {!isForced && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1.5,
                      textAlign: "center",
                      cursor: "pointer",
                      color: "#0C343D",
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline", color: "#00bcd4" },
                      transition: "color 0.2s ease",
                    }}
                    onClick={() => navigate("/Login")}
                  >
                    Back to Sign In
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
};

export default ChangePasswordPage;
